import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Cloud,
  Code2,
  Copy,
  Database,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  Monitor,
  Network,
  Plus,
  RefreshCw,
  Save,
  Server,
  ShieldCheck,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { clearOpenKey, getConfig, getRuntime, saveConfig, saveOpenKey, testProxy } from "../../api/settings";
import { getTaskSummary } from "../../api/tasks";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import type { TaskSummary } from "../../lib/types";
import { cn, formatDate, shortText } from "../../lib/utils";

type AppConfig = Record<string, unknown>;

type ProxyProviderDraft = {
  id: string;
  name: string;
  enabled: boolean;
  proxyUrl: string;
  notes: string;
};

type ProxyTestState = {
  status: "success" | "error";
  message: string;
  exitIp?: string;
  durationMs?: number;
};

type SettingsDraft = {
  activeProxyProvider: string;
  proxyProviders: ProxyProviderDraft[];
  cloakManagerBaseUrl: string;
  cloudBrowserUrl: string;
  browserManagerId: string;
  cloakStatusNote: string;
  stopAfterTask: boolean;
  consumerWorkerId: string;
  consumerWorkerName: string;
  consumerMaxConcurrency: string;
  consumerPollInterval: string;
  workerHeartbeatInterval: string;
  taskHeartbeatInterval: string;
  taskRetryDelayMs: string;
  managerHealthInterval: string;
  managerFailureThreshold: string;
  profileSyncInterval: string;
  profileSyncBatchSize: string;
  diskUsageMode: string;
  diskUsageInterval: string;
  managerStaleThreshold: string;
  inventoryStaleThreshold: string;
  longRunningThreshold: string;
  proxyFailureThreshold: string;
  queueProtocol: string;
  queueBaseUrl: string;
  queueVisibilityTimeoutMs: string;
  queuePullPath: string;
  queueAckPathTemplate: string;
  mailBaseUrl: string;
  mailPublicToken: string;
  mailPollInterval: string;
  mailPollTimeout: string;
  mailListSize: string;
  discordProtocolUrl: string;
  discordProtocolServerUrl: string;
  discordProtocolKey: string;
  jsonText: string;
};

const emptyDraft: SettingsDraft = {
  activeProxyProvider: "",
  proxyProviders: [],
  cloakManagerBaseUrl: "http://127.0.0.1:8080",
  cloudBrowserUrl: "https://browser.zhongwenmj.com",
  browserManagerId: "local-cloak-manager",
  cloakStatusNote: "",
  stopAfterTask: true,
  consumerWorkerId: "local-consumer-1",
  consumerWorkerName: "local cloak account consumer",
  consumerMaxConcurrency: "2",
  consumerPollInterval: "1",
  workerHeartbeatInterval: "30",
  taskHeartbeatInterval: "30",
  taskRetryDelayMs: "60000",
  managerHealthInterval: "15",
  managerFailureThreshold: "4",
  profileSyncInterval: "300",
  profileSyncBatchSize: "100",
  diskUsageMode: "scheduled",
  diskUsageInterval: "3600",
  managerStaleThreshold: "60",
  inventoryStaleThreshold: "600",
  longRunningThreshold: "240",
  proxyFailureThreshold: "3",
  queueProtocol: "cloudflare",
  queueBaseUrl: "",
  queueVisibilityTimeoutMs: "30000",
  queuePullPath: "/messages/pull",
  queueAckPathTemplate: "/messages/ack",
  mailBaseUrl: "",
  mailPublicToken: "",
  mailPollInterval: "5",
  mailPollTimeout: "120",
  mailListSize: "5",
  discordProtocolUrl: "https://discord.com",
  discordProtocolServerUrl: "",
  discordProtocolKey: "",
  jsonText: "{}"
};

const selectClass = "h-8 rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-slate-600";

function asRecord(value: unknown): AppConfig {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as AppConfig) : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function textValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function numberText(value: unknown, fallback: number): string {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : String(fallback);
}

function parseNumber(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function makeProviderId(name = "proxy"): string {
  return `${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "proxy"}-${Date.now()}`;
}

function providerName(provider: ProxyProviderDraft): string {
  return provider.name.trim() || provider.id;
}

function proxyUrlFromConfig(record: AppConfig): string {
  const direct = textValue(record.proxy_url || record.url);
  if (direct) return direct;
  const type = textValue(record.proxy_type || record.type || "socks5").trim() || "socks5";
  const host = textValue(record.host || record.server || record.proxy_host).trim();
  const port = textValue(record.port || record.proxy_port).trim();
  if (!host || !port) return "";
  const username = textValue(record.username || record.proxy_username).trim();
  const password = textValue(record.password || record.proxy_password);
  const auth = username ? `${encodeURIComponent(username)}${password ? `:${encodeURIComponent(password)}` : ""}@` : "";
  return `${type}://${auth}${host}:${port}`;
}

function providerFromConfig(item: unknown, index: number): ProxyProviderDraft {
  const record = asRecord(item);
  return {
    id: textValue(record.id) || textValue(record.name) || `proxy-${index + 1}`,
    name: textValue(record.name) || `proxy-${index + 1}`,
    enabled: record.enabled !== false && record.use_proxy !== false,
    proxyUrl: proxyUrlFromConfig(record),
    notes: textValue(record.notes || record.remark)
  };
}

function providerToConfig(provider: ProxyProviderDraft): AppConfig {
  return {
    id: provider.id,
    name: providerName(provider),
    enabled: provider.enabled,
    use_proxy: provider.enabled,
    proxy_url: provider.proxyUrl.trim(),
    notes: provider.notes.trim()
  };
}

function legacyProxyProfile(provider: ProxyProviderDraft): AppConfig {
  const config = providerToConfig(provider);
  return {
    ...config,
    proxy_url: provider.proxyUrl.trim()
  };
}

function firstProviderName(providers: ProxyProviderDraft[]): string {
  return providers[0] ? providerName(providers[0]) : "";
}

function configToDraft(config: AppConfig): SettingsDraft {
  const proxyProvidersSource = asArray(config.proxy_providers).length ? asArray(config.proxy_providers) : asArray(config.proxy_profiles);
  const proxyProviders = proxyProvidersSource.map(providerFromConfig);
  const cloak = asRecord(config.cloak_browser_manager);
  const consumer = asRecord(config.consumer);
  const queue = asRecord(config.cloudflare_queue);
  const mail = asRecord(config.mail_api);
  const discordProtocol = asRecord(config.discord_protocol);
  const firstName = firstProviderName(proxyProviders);
  return {
    activeProxyProvider: textValue(config.active_proxy_provider || config.active_proxy_profile || firstName),
    proxyProviders,
    cloakManagerBaseUrl: textValue(cloak.base_url || "http://127.0.0.1:8080"),
    cloudBrowserUrl: textValue(config.cloud_browser_url || "https://browser.zhongwenmj.com"),
    browserManagerId: textValue(cloak.browser_manager_id || "local-cloak-manager"),
    cloakStatusNote: textValue(cloak.status_note),
    stopAfterTask: cloak.stop_after_task !== false,
    consumerWorkerId: textValue(consumer.worker_id || "local-consumer-1"),
    consumerWorkerName: textValue(consumer.worker_name || "local cloak account consumer"),
    consumerMaxConcurrency: numberText(consumer.max_concurrency, 2),
    consumerPollInterval: numberText(consumer.poll_interval_seconds, 1),
    workerHeartbeatInterval: numberText(consumer.worker_heartbeat_interval_seconds, 30),
    taskHeartbeatInterval: numberText(consumer.task_heartbeat_interval_seconds, 30),
    taskRetryDelayMs: numberText(consumer.task_retry_delay_ms, 60000),
    managerHealthInterval: numberText(consumer.manager_health_interval_seconds, 15),
    managerFailureThreshold: numberText(consumer.manager_unreachable_failure_threshold, 4),
    profileSyncInterval: numberText(consumer.profile_sync_interval_seconds, 300),
    profileSyncBatchSize: numberText(consumer.profile_sync_batch_size, 100),
    diskUsageMode: textValue(consumer.disk_usage_mode || "scheduled"),
    diskUsageInterval: numberText(consumer.disk_usage_interval_seconds, 3600),
    managerStaleThreshold: numberText(consumer.manager_stale_threshold_seconds, 60),
    inventoryStaleThreshold: numberText(consumer.profile_inventory_stale_threshold_seconds, 600),
    longRunningThreshold: numberText(consumer.long_running_threshold_minutes, 240),
    proxyFailureThreshold: numberText(consumer.proxy_failure_threshold, 3),
    queueProtocol: textValue(queue.protocol || "cloudflare"),
    queueBaseUrl: textValue(queue.base_url),
    queueVisibilityTimeoutMs: numberText(queue.visibility_timeout_ms, 30000),
    queuePullPath: textValue(queue.pull_path || "/messages/pull"),
    queueAckPathTemplate: textValue(queue.ack_path_template || "/messages/ack"),
    mailBaseUrl: textValue(mail.base_url),
    mailPublicToken: textValue(mail.public_token),
    mailPollInterval: numberText(mail.poll_interval, 5),
    mailPollTimeout: numberText(mail.poll_timeout, 120),
    mailListSize: numberText(mail.list_size, 5),
    discordProtocolUrl: textValue(config.discord_login_url || discordProtocol.protocol_url || "https://discord.com"),
    discordProtocolServerUrl: textValue(config.discord_login_server_url || discordProtocol.server_url || discordProtocol.service_url || discordProtocol.api_url),
    discordProtocolKey: textValue(config.discord_login_SERVICE_TOKEN || discordProtocol.key || discordProtocol.api_key || discordProtocol.token),
    jsonText: JSON.stringify(config, null, 2)
  };
}

function draftToConfig(baseConfig: AppConfig, draft: SettingsDraft): AppConfig {
  const config = draft.jsonText.trim() ? (JSON.parse(draft.jsonText) as AppConfig) : structuredClone(baseConfig);
  const providers = draft.proxyProviders.map(providerToConfig);
  const legacyProfiles = draft.proxyProviders.map(legacyProxyProfile);
  const activeName = draft.activeProxyProvider.trim() || firstProviderName(draft.proxyProviders);
  const activeProvider = draft.proxyProviders.find((provider) => providerName(provider) === activeName) || draft.proxyProviders[0];

  config.proxy_providers = providers;
  config.active_proxy_provider = activeName;
  config.proxy_profiles = legacyProfiles;
  config.active_proxy_profile = activeName;
  config.proxy = activeProvider ? legacyProxyProfile(activeProvider) : {};

  config.cloak_browser_manager = {
    ...asRecord(config.cloak_browser_manager),
    base_url: draft.cloakManagerBaseUrl.trim() || "http://127.0.0.1:8080",
    browser_manager_id: draft.browserManagerId.trim() || "local-cloak-manager",
    status_note: draft.cloakStatusNote.trim(),
    stop_after_task: draft.stopAfterTask
  };
  config.cloud_browser_url = draft.cloudBrowserUrl.trim() || "https://browser.zhongwenmj.com";

  config.consumer = {
    ...asRecord(config.consumer),
    worker_id: draft.consumerWorkerId.trim(),
    worker_name: draft.consumerWorkerName.trim(),
    browser_manager_id: draft.browserManagerId.trim() || "local-cloak-manager",
    max_concurrency: parseNumber(draft.consumerMaxConcurrency, 2),
    poll_interval_seconds: parseNumber(draft.consumerPollInterval, 1),
    worker_heartbeat_interval_seconds: parseNumber(draft.workerHeartbeatInterval, 30),
    task_heartbeat_interval_seconds: parseNumber(draft.taskHeartbeatInterval, 30),
    task_retry_delay_ms: parseNumber(draft.taskRetryDelayMs, 60000),
    manager_health_interval_seconds: parseNumber(draft.managerHealthInterval, 15),
    manager_unreachable_failure_threshold: parseNumber(draft.managerFailureThreshold, 4),
    profile_sync_interval_seconds: parseNumber(draft.profileSyncInterval, 300),
    profile_sync_batch_size: parseNumber(draft.profileSyncBatchSize, 100),
    disk_usage_mode: draft.diskUsageMode || "scheduled",
    disk_usage_interval_seconds: parseNumber(draft.diskUsageInterval, 3600),
    manager_stale_threshold_seconds: parseNumber(draft.managerStaleThreshold, 60),
    profile_inventory_stale_threshold_seconds: parseNumber(draft.inventoryStaleThreshold, 600),
    long_running_threshold_minutes: parseNumber(draft.longRunningThreshold, 240),
    proxy_failure_threshold: parseNumber(draft.proxyFailureThreshold, 3)
  };

  config.cloudflare_queue = {
    ...asRecord(config.cloudflare_queue),
    protocol: draft.queueProtocol.trim() || "cloudflare",
    base_url: draft.queueBaseUrl.trim(),
    visibility_timeout_ms: parseNumber(draft.queueVisibilityTimeoutMs, 30000),
    pull_path: draft.queuePullPath.trim() || "/messages/pull",
    ack_path_template: draft.queueAckPathTemplate.trim() || "/messages/ack"
  };

  config.mail_api = {
    ...asRecord(config.mail_api),
    base_url: draft.mailBaseUrl.trim(),
    public_token: draft.mailPublicToken.trim(),
    poll_interval: parseNumber(draft.mailPollInterval, 5),
    poll_timeout: parseNumber(draft.mailPollTimeout, 120),
    list_size: parseNumber(draft.mailListSize, 5)
  };

  config.discord_protocol = {
    ...asRecord(config.discord_protocol),
    protocol_url: draft.discordProtocolUrl.trim() || "https://discord.com",
    server_url: draft.discordProtocolServerUrl.trim(),
    key: draft.discordProtocolKey.trim()
  };
  config.discord_login_url = draft.discordProtocolUrl.trim() || "https://discord.com";
  config.discord_login_server_url = draft.discordProtocolServerUrl.trim();
  config.discord_login_SERVICE_TOKEN = draft.discordProtocolKey.trim();

  const database = asRecord(config.database);
  const d1 = asRecord(database.d1);
  delete d1.api_token;
  database.d1 = d1;
  config.database = database;
  return config;
}

function changedAreas(current: AppConfig, draft: SettingsDraft): string[] {
  const next = draftToConfig(current, draft);
  const areas: string[] = [];
  if (JSON.stringify(next.proxy_providers ?? []) !== JSON.stringify(current.proxy_providers ?? [])) areas.push("代理商");
  if (textValue(next.active_proxy_provider) !== textValue(current.active_proxy_provider || current.active_proxy_profile)) areas.push("默认代理");
  if (JSON.stringify(next.cloak_browser_manager ?? {}) !== JSON.stringify(current.cloak_browser_manager ?? {})) areas.push("CloakBrowser");
  if (textValue(next.cloud_browser_url) !== textValue(current.cloud_browser_url || "https://browser.zhongwenmj.com")) areas.push("云端浏览器");
  if (JSON.stringify(next.consumer ?? {}) !== JSON.stringify(current.consumer ?? {})) areas.push("执行机");
  if (JSON.stringify(next.cloudflare_queue ?? {}) !== JSON.stringify(current.cloudflare_queue ?? {})) areas.push("Queue");
  if (JSON.stringify(next.mail_api ?? {}) !== JSON.stringify(current.mail_api ?? {})) areas.push("邮箱验证码");
  if (
    JSON.stringify(next.discord_protocol ?? {}) !== JSON.stringify(current.discord_protocol ?? {}) ||
    textValue(next.discord_login_server_url) !== textValue(current.discord_login_server_url) ||
    textValue(next.discord_login_SERVICE_TOKEN) !== textValue(current.discord_login_SERVICE_TOKEN)
  ) areas.push("Discord 协议登录");
  return areas;
}

function Panel({ title, icon, action, children, className }: { title: string; icon: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("min-w-0 rounded-md border bg-white", className)}>
      <div className="flex h-10 items-center justify-between border-b px-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          {icon}
          {title}
        </div>
        {action}
      </div>
      <div className="p-3">{children}</div>
    </section>
  );
}

function Field({ label, note, children }: { label: string; note?: string; children: ReactNode }) {
  return (
    <label className="grid gap-1 text-xs">
      <span className="font-medium text-slate-600">{label}</span>
      {children}
      {note ? <span className="break-words text-[11px] leading-4 text-slate-500 [overflow-wrap:anywhere]">{note}</span> : null}
    </label>
  );
}

function Readout({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="truncate text-xs font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function workerStatus(summary?: TaskSummary) {
  if (!summary || summary.total === 0) return { label: "暂无任务", className: "bg-slate-100 text-slate-700", note: "D1 里还没有任务记录" };
  if (summary.running > 0) return { label: "执行中", className: "bg-blue-50 text-blue-700", note: "检测到 running 任务，本地 consumer 可能正在执行" };
  if (summary.queued > 0) return { label: "待消费", className: "bg-amber-50 text-amber-700", note: "有 queued 任务，请确认本地 consumer 已启动并能 pull Queue" };
  if (summary.failed > 0) return { label: "有失败", className: "bg-rose-50 text-rose-700", note: "最近没有活跃任务，但存在失败任务需要排查" };
  return { label: "空闲", className: "bg-emerald-50 text-emerald-700", note: "当前没有 queued/running 任务" };
}

function proxySummary(provider: ProxyProviderDraft): string {
  const url = provider.proxyUrl.trim();
  if (!url) return "未填代理 URL";
  try {
    const parsed = new URL(url.replaceAll("{random-8-number}", "00000000"));
    return `${parsed.protocol.replace(":", "")} / ${parsed.hostname}:${parsed.port || "-"}`;
  } catch {
    return "URL 格式错误";
  }
}

function maskProxyUrl(value: string): string {
  const text = value.trim();
  if (!text) return "-";
  try {
    const parsed = new URL(text.replaceAll("{random-8-number}", "00000000"));
    if (parsed.password) parsed.password = "***";
    return parsed.toString().replaceAll("00000000", "{random-8-number}");
  } catch {
    return text.length > 120 ? `${text.slice(0, 80)}...${text.slice(-24)}` : text;
  }
}

export function SettingsPage() {
  const queryClient = useQueryClient();
  const runtime = useQuery({ queryKey: ["runtime"], queryFn: getRuntime });
  const config = useQuery({ queryKey: ["config"], queryFn: getConfig });
  const taskSummary = useQuery({ queryKey: ["task-summary"], queryFn: getTaskSummary, refetchInterval: 15000 });
  const [draft, setDraft] = useState<SettingsDraft>(emptyDraft);
  const [openKey, setOpenKey] = useState("");
  const [showOpenKey, setShowOpenKey] = useState(false);
  const [pendingSave, setPendingSave] = useState<{ config: AppConfig; areas: string[] } | null>(null);
  const [proxyTestResults, setProxyTestResults] = useState<Record<string, ProxyTestState>>({});

  useEffect(() => {
    if (config.data?.config) setDraft(configToDraft(config.data.config));
  }, [config.data?.config]);

  const activeProvider = useMemo(() => draft.proxyProviders.find((provider) => providerName(provider) === draft.activeProxyProvider) || draft.proxyProviders[0], [draft]);
  const summary = useMemo(() => {
    if (!config.data?.config) return ["配置加载中"];
    try {
      const areas = changedAreas(config.data.config, draft);
      return areas.length ? areas : ["无未保存变更"];
    } catch (error) {
      return [error instanceof Error ? error.message : "配置格式错误"];
    }
  }, [config.data?.config, draft]);

  const taskStats = taskSummary.data?.summary;
  const inferredWorkerStatus = workerStatus(taskStats);
  const runtimeDatabase = asRecord(runtime.data?.database);
  const runtimeD1 = asRecord(runtimeDatabase.d1);
  const runtimeQueue = asRecord(runtime.data?.cloudflare_queue);
  const runtimeCloak = asRecord(runtime.data?.cloak_browser_manager);
  const runtimeConsumer = asRecord(runtime.data?.consumer);
  const runtimePaths = asRecord(runtime.data?.paths);
  const runtimeSecurity = asRecord(runtime.data?.security);
  const runtimeApp = asRecord(runtime.data?.app);

  const mutation = useMutation({
    mutationFn: saveConfig,
    onSuccess: (response) => {
      setDraft(configToDraft(response.config));
      setPendingSave(null);
      toast.success("配置已保存");
      void queryClient.invalidateQueries({ queryKey: ["config"] });
      void queryClient.invalidateQueries({ queryKey: ["runtime"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "保存失败")
  });

  const openKeyMutation = useMutation({
    mutationFn: saveOpenKey,
    onSuccess: () => {
      setOpenKey("");
      toast.success("Open Key 已保存，旧 Open Key 已立即失效");
      void queryClient.invalidateQueries({ queryKey: ["runtime"] });
      void queryClient.invalidateQueries({ queryKey: ["auth-status"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Open Key 保存失败")
  });

  const clearOpenKeyMutation = useMutation({
    mutationFn: clearOpenKey,
    onSuccess: () => {
      setOpenKey("");
      toast.success("Open Key 已清除");
      void queryClient.invalidateQueries({ queryKey: ["runtime"] });
      void queryClient.invalidateQueries({ queryKey: ["auth-status"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Open Key 清除失败")
  });

  const proxyTestMutation = useMutation({
    mutationFn: ({ id, proxyUrl }: { id: string; proxyUrl: string }) => testProxy(proxyUrl).then((result) => ({ id, result })),
    onSuccess: ({ id, result }) => {
      const state: ProxyTestState = result.ok
        ? {
            status: "success",
            message: `出口 ${result.exit_ip || "-"}，耗时 ${result.duration_ms ?? "-"}ms`,
            exitIp: result.exit_ip,
            durationMs: result.duration_ms
          }
        : { status: "error", message: result.error || "代理测试失败" };
      setProxyTestResults((current) => ({ ...current, [id]: state }));
      if (result.ok) toast.success(state.message);
      else toast.error(state.message);
    },
    onError: (error, variables) => {
      const message = error instanceof Error ? error.message : "代理测试失败";
      setProxyTestResults((current) => ({ ...current, [variables.id]: { status: "error", message } }));
      toast.error(message);
    }
  });

  function patchDraft(patch: Partial<SettingsDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function updateProvider(id: string, patch: Partial<ProxyProviderDraft>) {
    setDraft((current) => ({
      ...current,
      proxyProviders: current.proxyProviders.map((provider) => (provider.id === id ? { ...provider, ...patch } : provider))
    }));
  }

  function addProvider() {
    const id = makeProviderId("proxy");
    const nextProvider: ProxyProviderDraft = {
      id,
      name: `proxy-${draft.proxyProviders.length + 1}`,
      enabled: true,
      proxyUrl: "",
      notes: ""
    };
    setDraft((current) => ({
      ...current,
      activeProxyProvider: providerName(nextProvider),
      proxyProviders: [...current.proxyProviders, nextProvider]
    }));
  }

  function removeProvider(id: string) {
    setDraft((current) => {
      const next = current.proxyProviders.filter((provider) => provider.id !== id);
      const activeStillExists = next.some((provider) => providerName(provider) === current.activeProxyProvider);
      return {
        ...current,
        proxyProviders: next,
        activeProxyProvider: activeStillExists ? current.activeProxyProvider : firstProviderName(next)
      };
    });
  }

  function save() {
    if (!config.data?.config) {
      toast.error("配置还没有加载完成");
      return;
    }
    try {
      for (const provider of draft.proxyProviders) {
        const proxyUrl = provider.proxyUrl.trim();
        if (proxyUrl) new URL(proxyUrl.replaceAll("{random-8-number}", "00000000"));
      }
      if (draft.cloakManagerBaseUrl.trim()) new URL(draft.cloakManagerBaseUrl.trim());
      if (draft.queueBaseUrl.trim()) new URL(draft.queueBaseUrl.trim());
      if (draft.mailBaseUrl.trim()) new URL(draft.mailBaseUrl.trim());
      const nextConfig = draftToConfig(config.data.config, draft);
      const areas = changedAreas(config.data.config, draft);
      if (!areas.length) {
        toast.info("没有未保存变更");
        return;
      }
      setPendingSave({ config: nextConfig, areas });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "配置格式错误");
    }
  }

  async function copyConsumerEnv() {
    const lines = [
      `CLOAK_CONSUMER_CLOAK_MANAGER_BASE_URL=${draft.cloakManagerBaseUrl || "http://127.0.0.1:8080"}`,
      `CLOAK_CONSUMER_BROWSER_MANAGER_ID=${draft.browserManagerId || "local-cloak-manager"}`,
      `CLOAK_CONSUMER_WORKER_ID=${draft.consumerWorkerId || "local-consumer-1"}`,
      `CLOAK_CONSUMER_WORKER_NAME=${draft.consumerWorkerName || "local cloak account consumer"}`,
      `CLOAK_CONSUMER_MAX_CONCURRENCY=${draft.consumerMaxConcurrency || "2"}`,
      `CLOAK_CONSUMER_MANAGER_HEALTH_INTERVAL_SECONDS=${draft.managerHealthInterval || "15"}`,
      `CLOAK_CONSUMER_MANAGER_UNREACHABLE_FAILURE_THRESHOLD=${draft.managerFailureThreshold || "4"}`,
      `CLOAK_CONSUMER_PROFILE_SYNC_INTERVAL_SECONDS=${draft.profileSyncInterval || "300"}`,
      `CLOAK_CONSUMER_PROFILE_SYNC_BATCH_SIZE=${draft.profileSyncBatchSize || "100"}`,
      `CLOAK_CONSUMER_DISK_USAGE_MODE=${draft.diskUsageMode || "scheduled"}`,
      `CLOAK_CONSUMER_DISK_USAGE_INTERVAL_SECONDS=${draft.diskUsageInterval || "3600"}`,
      `CLOAK_CONSUMER_QUEUE_PROTOCOL=${draft.queueProtocol || "cloudflare"}`,
      `CLOAK_CONSUMER_QUEUE_BASE_URL=${draft.queueBaseUrl}`,
      "CLOAK_CONSUMER_QUEUE_AUTH_TOKEN=<fill-on-execution-machine>",
      `CLOAK_CONSUMER_QUEUE_VISIBILITY_TIMEOUT_MS=${draft.queueVisibilityTimeoutMs || "30000"}`
    ].join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("已复制执行机 .env 片段");
    } catch {
      toast.error("复制失败，请从高级 JSON 或页面字段手动复制");
    }
  }

  function runProxyTest(provider: ProxyProviderDraft) {
    const proxyUrl = provider.proxyUrl.trim();
    if (!proxyUrl) {
      toast.error("请先填写代理 URL");
      return;
    }
    proxyTestMutation.mutate({ id: provider.id, proxyUrl });
  }

  const runtimeFactsPanel = (
    <Panel title="运行事实" icon={<Database size={14} />}>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Readout label="D1 Binding" value={textValue(runtimeD1.binding || "DB")} />
        <Readout label="配置存储" value={textValue(runtimeDatabase.config_storage || "database")} />
        <Readout label="版本" value={textValue(runtimeApp.version || "-")} />
        <Readout label="Runtime" value={textValue(runtimePaths.runtime || "cloudflare-worker")} />
        <Readout label="APP_ENV" value={textValue(runtimePaths.app_env || "-")} />
        <Readout label="Queue Binding" value={textValue(runtimeQueue.binding || "TASK_QUEUE")} />
        <Readout label="Cloak URL" value={textValue(runtimeCloak.base_url || draft.cloakManagerBaseUrl)} />
        <Readout label="Consumer ID" value={textValue(runtimeConsumer.worker_id || draft.consumerWorkerId)} />
        <Readout label="D1 Token" value={runtimeD1.api_token_configured ? "已通过环境配置" : "不在控制台保存"} />
      </div>
    </Panel>
  );

  const consumerStatusPanel = (
    <Panel title="本地 consumer 状态" icon={<Activity size={14} />} action={<Badge className="bg-slate-100 text-slate-700">从任务表推断</Badge>}>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
        <Readout label="状态" value={<span className={cn("inline-flex rounded px-1.5 py-0.5 text-[11px]", inferredWorkerStatus.className)}>{taskSummary.isLoading ? "加载中" : inferredWorkerStatus.label}</span>} />
        <Readout label="Queued" value={taskStats?.queued ?? "-"} />
        <Readout label="Running" value={taskStats?.running ?? "-"} />
        <Readout label="Failed" value={taskStats?.failed ?? "-"} />
        <Readout label="Active" value={taskStats?.active ?? "-"} />
        <Readout label="最近更新" value={formatDate(taskStats?.last_task_updated_at)} />
      </div>
      <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        {taskSummary.isError ? <span className="text-rose-700">读取任务汇总失败，请检查 D1 binding 和 tasks 表。</span> : <span>{inferredWorkerStatus.note}</span>}
      </div>
      {taskStats?.latest_failed_task ? (
        <div className="mt-2 rounded-md border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          <div className="font-semibold">
            最近失败：{taskStats.latest_failed_task.task_type} / {shortText(taskStats.latest_failed_task.id, 6)}
          </div>
          <div className="mt-1 break-words leading-5 [overflow-wrap:anywhere]">
            {taskStats.latest_failed_task.error_message || taskStats.latest_failed_task.execute_message || "暂无错误信息"}
          </div>
        </div>
      ) : null}
    </Panel>
  );

  const openKeyPanel = (
    <Panel
      title="外部 API Open Key"
      icon={<KeyRound size={14} />}
      action={
        <Badge className={runtimeSecurity.open_key_configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}>
          {runtimeSecurity.open_key_configured ? "已配置" : "未配置"}
        </Badge>
      }
    >
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-end">
        <div className="grid gap-1 text-xs">
          <label htmlFor="open-key" className="font-medium text-slate-600">Open Key</label>
          <div className="flex gap-2">
            <Input
              id="open-key"
              type={showOpenKey ? "text" : "password"}
              autoComplete="new-password"
              value={openKey}
              onChange={(event) => setOpenKey(event.target.value)}
              placeholder={runtimeSecurity.open_key_configured ? "输入新值以轮换 Open Key" : "输入新的 Open Key"}
            />
            <Button type="button" aria-label={showOpenKey ? "隐藏 Open Key" : "显示 Open Key"} aria-pressed={showOpenKey} onClick={() => setShowOpenKey((current) => !current)}>
              {showOpenKey ? <EyeOff size={14} /> : <Eye size={14} />}
              {showOpenKey ? "隐藏" : "显示"}
            </Button>
          </div>
          <span className="break-words text-[11px] leading-4 text-slate-500 [overflow-wrap:anywhere]">
            至少 24 个字符；保存后只存储 SHA-256，旧值不会回显。外部程序通过 Authorization: Bearer &lt;open_key&gt; 调用 API。
          </span>
        </div>
        <Button
          disabled={openKeyMutation.isPending || openKey.trim().length < 24}
          onClick={() => openKeyMutation.mutate(openKey.trim())}
          className="bg-slate-900 text-white hover:bg-slate-800"
        >
          <Save size={14} />
          {openKeyMutation.isPending ? "保存中..." : runtimeSecurity.open_key_configured ? "轮换" : "保存"}
        </Button>
        <Button
          disabled={clearOpenKeyMutation.isPending || !runtimeSecurity.open_key_configured}
          onClick={() => {
            if (window.confirm("确认清除 Open Key？使用该 Key 的外部程序会立即失去访问权限。")) clearOpenKeyMutation.mutate();
          }}
          className="border-rose-200 text-rose-700 hover:bg-rose-50"
        >
          <Trash2 size={14} />
          {clearOpenKeyMutation.isPending ? "清除中..." : "清除"}
        </Button>
      </div>
    </Panel>
  );

  return (
    <div className="grid h-full grid-rows-[auto_1fr] gap-3 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="bg-slate-900 text-white">运行配置</Badge>
          <Badge className="bg-blue-50 text-blue-700">DrissionPage + CloakBrowser</Badge>
          {summary[0] === "无未保存变更" ? (
            <Badge className="bg-emerald-50 text-emerald-700">
              <CheckCircle2 size={12} />
              无未保存变更
            </Badge>
          ) : (
            <Badge className="bg-amber-50 text-amber-700">
              <AlertTriangle size={12} />
              {summary.join(" / ")}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: ["config"] });
              void queryClient.invalidateQueries({ queryKey: ["runtime"] });
              void queryClient.invalidateQueries({ queryKey: ["task-summary"] });
            }}
          >
            <RefreshCw size={14} />
            刷新
          </Button>
          <Button disabled={mutation.isPending || config.isLoading} onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">
            <Save size={14} />
            保存配置
          </Button>
        </div>
      </div>

      <div className="min-h-0 overflow-auto">
        <div className="grid content-start gap-3">
          {runtimeFactsPanel}
          {openKeyPanel}
          {consumerStatusPanel}
          <div className="grid gap-3 xl:grid-cols-[minmax(0,1.1fr)_minmax(420px,0.9fr)]">
          <div className="grid content-start gap-3">
            <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
              <Readout label="浏览器后端" value="cloakbrowser" />
              <Readout label="代理商" value={`${draft.activeProxyProvider || "-"} / ${draft.proxyProviders.length}`} />
              <Readout label="Consumer" value={draft.consumerWorkerId || "-"} />
              <Readout label="Queue" value={draft.queueProtocol || "cloudflare"} />
            </div>

            <Panel title="代理商配置" icon={<Network size={14} />} action={<Button onClick={addProvider}><Plus size={14} />添加代理商</Button>}>
              <div className="mb-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                <Field label="默认代理商">
                  <select className={selectClass} value={draft.activeProxyProvider} onChange={(event) => patchDraft({ activeProxyProvider: event.target.value })}>
                    {draft.proxyProviders.length ? null : <option value="">未配置</option>}
                    {draft.proxyProviders.map((provider) => (
                      <option key={provider.id} value={providerName(provider)}>
                        {providerName(provider)}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Readout label="当前代理" value={activeProvider ? proxySummary(activeProvider) : "-"} />
                  <Readout label="启用" value={activeProvider?.enabled ? "yes" : "no"} />
                </div>
              </div>

              <div className="grid gap-3">
                {draft.proxyProviders.length ? null : (
                  <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-xs text-slate-500">
                    还没有代理商。点击“添加代理商”后填写一条 socks5 代理 URL。
                  </div>
                )}
                {draft.proxyProviders.map((provider) => (
                  <div key={provider.id} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <input type="checkbox" checked={provider.enabled} onChange={(event) => updateProvider(provider.id, { enabled: event.target.checked })} />
                        <span className="truncate text-xs font-semibold text-slate-800">{providerName(provider)}</span>
                        {providerName(provider) === draft.activeProxyProvider ? <Badge className="bg-blue-50 text-blue-700">默认</Badge> : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button disabled={proxyTestMutation.isPending} onClick={() => runProxyTest(provider)}>
                          <ShieldCheck size={14} />
                          测试
                        </Button>
                        <Button onClick={() => removeProvider(provider.id)}>
                          <Trash2 size={14} />
                          删除
                        </Button>
                      </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                      <Field label="名称">
                        <Input value={provider.name} onChange={(event) => updateProvider(provider.id, { name: event.target.value })} />
                      </Field>
                      <label className="grid gap-1 text-xs">
                        <span className="font-medium text-slate-600">
                          代理 URL
                          <span className="ml-2 font-normal text-[11px] text-slate-500">支持 {"{random-8-number}"}，执行任务或测试时替换为 8 位随机数字。</span>
                        </span>
                        <Input
                          value={provider.proxyUrl}
                          onChange={(event) => updateProvider(provider.id, { proxyUrl: event.target.value })}
                          placeholder="socks5://user:pass-session-{random-8-number}@gate.example.com:1000"
                        />
                      </label>
                    </div>
                    <div className="mt-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] leading-5 text-slate-500">
                      <span className="font-medium text-slate-600">摘要：</span>
                      <span className="font-mono">{maskProxyUrl(provider.proxyUrl)}</span>
                    </div>
                    {proxyTestResults[provider.id] ? (
                      <div
                        className={cn(
                          "mt-2 rounded-md border px-2 py-1.5 text-[11px] leading-5",
                          proxyTestResults[provider.id].status === "success"
                            ? "border-emerald-100 bg-emerald-50 text-emerald-800"
                            : "border-rose-100 bg-rose-50 text-rose-800"
                        )}
                      >
                        {proxyTestResults[provider.id].message}
                      </div>
                    ) : null}
                    <div className="mt-3">
                      <Field label="备注" note="用于记录代理商套餐、地区、出口规则或注意事项。">
                        <Input value={provider.notes} onChange={(event) => updateProvider(provider.id, { notes: event.target.value })} />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="邮箱验证码服务" icon={<Mail size={14} />}>
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px_160px_160px]">
                <Field label="Base URL">
                  <Input value={draft.mailBaseUrl} onChange={(event) => patchDraft({ mailBaseUrl: event.target.value })} />
                </Field>
                <Field label="Public Token">
                  <Input value={draft.mailPublicToken} onChange={(event) => patchDraft({ mailPublicToken: event.target.value })} />
                </Field>
                <Field label="轮询间隔 秒">
                  <Input type="number" min={1} value={draft.mailPollInterval} onChange={(event) => patchDraft({ mailPollInterval: event.target.value })} />
                </Field>
                <Field label="每次数量">
                  <Input type="number" min={1} value={draft.mailListSize} onChange={(event) => patchDraft({ mailListSize: event.target.value })} />
                </Field>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                <Field label="超时 秒">
                  <Input type="number" min={1} value={draft.mailPollTimeout} onChange={(event) => patchDraft({ mailPollTimeout: event.target.value })} />
                </Field>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                  仅用于 Discord 邮箱换绑验证码/验证链接获取。控制台保存配置，不在这里直接调用第三方邮箱服务。
                </div>
              </div>
            </Panel>
            <Panel title="Discord 协议登录" icon={<KeyRound size={14} />}>
              <div className="grid gap-3">
                <Field label="协议地址（discord_login_url）" note="Discord 协议登录目标地址，默认使用官方地址。">
                  <Input value={draft.discordProtocolUrl} onChange={(event) => patchDraft({ discordProtocolUrl: event.target.value })} placeholder="https://discord.com" />
                </Field>
                <Field label="协议服务器地址（discord_login_server_url）" note="hCaptcha 协议服务地址，执行机将直接请求该地址。">
                  <Input value={draft.discordProtocolServerUrl} onChange={(event) => patchDraft({ discordProtocolServerUrl: event.target.value })} placeholder="https://api.example.com/solve" />
                </Field>
                <Field label="协议密钥（discord_login_SERVICE_TOKEN）">
                  <Input type="password" value={draft.discordProtocolKey} onChange={(event) => patchDraft({ discordProtocolKey: event.target.value })} placeholder="输入协议服务密钥" />
                </Field>
              </div>
            </Panel>
          </div>

          <div className="grid content-start gap-3">
            <Panel title="执行机 / Consumer" icon={<Server size={14} />}>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Worker ID">
                  <Input value={draft.consumerWorkerId} onChange={(event) => patchDraft({ consumerWorkerId: event.target.value })} />
                </Field>
                <Field label="Worker Name">
                  <Input value={draft.consumerWorkerName} onChange={(event) => patchDraft({ consumerWorkerName: event.target.value })} />
                </Field>
                <Field label="最大并发">
                  <Input type="number" min={1} value={draft.consumerMaxConcurrency} onChange={(event) => patchDraft({ consumerMaxConcurrency: event.target.value })} />
                </Field>
                <Field label="拉取间隔 秒">
                  <Input type="number" min={0.1} step={0.1} value={draft.consumerPollInterval} onChange={(event) => patchDraft({ consumerPollInterval: event.target.value })} />
                </Field>
                <Field label="Worker Heartbeat 秒">
                  <Input type="number" min={1} value={draft.workerHeartbeatInterval} onChange={(event) => patchDraft({ workerHeartbeatInterval: event.target.value })} />
                </Field>
                <Field label="Task Heartbeat 秒">
                  <Input type="number" min={1} value={draft.taskHeartbeatInterval} onChange={(event) => patchDraft({ taskHeartbeatInterval: event.target.value })} />
                </Field>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                <Field label="失败重试延迟 ms">
                  <Input type="number" min={0} value={draft.taskRetryDelayMs} onChange={(event) => patchDraft({ taskRetryDelayMs: event.target.value })} />
                </Field>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
                  systemd 执行机仍以 `.env` 为实际启动源；这里保存的是控制台侧目标配置和运维记录。修改后需要同步到执行机并重启 consumer 才会真正生效。
                </div>
              </div>
              <div className="mt-4 border-t border-slate-200 pt-3">
                <div className="mb-3 text-xs font-semibold text-slate-700">Manager 健康与清单同步</div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <Field label="健康心跳间隔 秒"><Input type="number" min={2} value={draft.managerHealthInterval} onChange={(event) => patchDraft({ managerHealthInterval: event.target.value })} /></Field>
                  <Field label="失联连续失败次数"><Input type="number" min={1} value={draft.managerFailureThreshold} onChange={(event) => patchDraft({ managerFailureThreshold: event.target.value })} /></Field>
                  <Field label="Manager 状态过期 秒"><Input type="number" min={30} value={draft.managerStaleThreshold} onChange={(event) => patchDraft({ managerStaleThreshold: event.target.value })} /></Field>
                  <Field label="Profile 清单同步间隔 秒"><Input type="number" min={10} value={draft.profileSyncInterval} onChange={(event) => patchDraft({ profileSyncInterval: event.target.value })} /></Field>
                  <Field label="清单上传批次"><Input type="number" min={10} max={200} value={draft.profileSyncBatchSize} onChange={(event) => patchDraft({ profileSyncBatchSize: event.target.value })} /></Field>
                  <Field label="清单状态过期 秒"><Input type="number" min={60} value={draft.inventoryStaleThreshold} onChange={(event) => patchDraft({ inventoryStaleThreshold: event.target.value })} /></Field>
                </div>
              </div>
              <div className="mt-4 border-t border-slate-200 pt-3">
                <div className="mb-3 text-xs font-semibold text-slate-700">磁盘统计与异常规则</div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <Field label="磁盘统计模式"><select className={selectClass} value={draft.diskUsageMode} onChange={(event) => patchDraft({ diskUsageMode: event.target.value })}><option value="scheduled">定时统计</option><option value="on_demand">仅手动</option><option value="off">关闭</option></select></Field>
                  <Field label="磁盘统计间隔 秒"><Input type="number" min={60} disabled={draft.diskUsageMode !== "scheduled"} value={draft.diskUsageInterval} onChange={(event) => patchDraft({ diskUsageInterval: event.target.value })} /></Field>
                  <Field label="长期运行阈值 分钟"><Input type="number" min={5} value={draft.longRunningThreshold} onChange={(event) => patchDraft({ longRunningThreshold: event.target.value })} /></Field>
                  <Field label="连续代理失败阈值"><Input type="number" min={1} value={draft.proxyFailureThreshold} onChange={(event) => patchDraft({ proxyFailureThreshold: event.target.value })} /></Field>
                </div>
              </div>
            </Panel>

            <Panel title="Cloudflare Queue" icon={<Cloud size={14} />}>
              <div className="mb-3 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-800">
                Worker 内部创建任务时使用 `TASK_QUEUE` binding 投递；这里的 HTTP Base URL 是给 Python consumer 从执行机拉取 Queue 用的。
                Cloudflare API token 不保存在控制台，只放执行机 `.env` 的 `CLOAK_CONSUMER_QUEUE_AUTH_TOKEN`。
              </div>
              <div className="grid gap-3">
                <Field label="Queue HTTP Base URL" note="Cloudflare Queue REST pull endpoint base，token 不在控制台保存。">
                  <Input value={draft.queueBaseUrl} onChange={(event) => patchDraft({ queueBaseUrl: event.target.value })} />
                </Field>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Protocol">
                    <select className={selectClass} value={draft.queueProtocol} onChange={(event) => patchDraft({ queueProtocol: event.target.value })}>
                      <option value="cloudflare">cloudflare</option>
                      <option value="local">local</option>
                    </select>
                  </Field>
                  <Field label="Visibility Timeout ms">
                    <Input type="number" min={1000} value={draft.queueVisibilityTimeoutMs} onChange={(event) => patchDraft({ queueVisibilityTimeoutMs: event.target.value })} />
                  </Field>
                  <Field label="Pull Path">
                    <Input value={draft.queuePullPath} onChange={(event) => patchDraft({ queuePullPath: event.target.value })} />
                  </Field>
                  <Field label="Ack Path">
                    <Input value={draft.queueAckPathTemplate} onChange={(event) => patchDraft({ queueAckPathTemplate: event.target.value })} />
                  </Field>
                </div>
              </div>
            </Panel>

            <Panel title="高级 JSON" icon={<Code2 size={14} />}>
              <details className="min-w-0">
                <summary className="cursor-pointer text-xs font-medium text-slate-700">完整 app_config</summary>
                <Textarea
                  className="mt-2 block min-h-72 w-full resize-y overflow-auto whitespace-pre font-mono leading-5"
                  wrap="off"
                  value={draft.jsonText}
                  onChange={(event) => patchDraft({ jsonText: event.target.value })}
                />
              </details>
            </Panel>

            <Panel
              title="CloakBrowser-Manager / 执行机启动参数"
              icon={<Monitor size={14} />}
              action={
                <Button onClick={() => void copyConsumerEnv()}>
                  <Copy size={14} />
                  复制 .env
                </Button>
              }
            >
              <div className="mb-3 rounded-md border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                这组值不会被 Cloudflare Worker 直接访问，也不会自动修改 systemd。实际生效点是执行机
                <span className="font-mono"> execution/.env </span>
                里的 `CLOAK_CONSUMER_CLOAK_MANAGER_BASE_URL` 和 `CLOAK_CONSUMER_BROWSER_MANAGER_ID`；保存到控制台主要用于记录目标配置、生成绑定标识和后续运维对齐。
              </div>
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
                <Field label="Manager Base URL" note="写入执行机 .env 后才会被 Python consumer 使用；线上 Worker 不会直连这个地址。">
                  <Input value={draft.cloakManagerBaseUrl} onChange={(event) => patchDraft({ cloakManagerBaseUrl: event.target.value })} />
                </Field>
                <Field label="云端浏览器 URL" note="页面顶部“云端浏览器”入口使用此地址。">
                  <Input value={draft.cloudBrowserUrl} onChange={(event) => patchDraft({ cloudBrowserUrl: event.target.value })} placeholder="https://browser.zhongwenmj.com" />
                </Field>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
                <Field label="Browser Manager ID" note="用于 profile 绑定记录和 consumer 默认 browser_manager_id。">
                  <Input value={draft.browserManagerId} onChange={(event) => patchDraft({ browserManagerId: event.target.value })} />
                </Field>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
                <Field label="状态备注">
                  <Input value={draft.cloakStatusNote} onChange={(event) => patchDraft({ cloakStatusNote: event.target.value })} placeholder="例如：本机 8080 已启动，扩展由 profile 模板维护" />
                </Field>
                <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <input type="checkbox" checked={draft.stopAfterTask} onChange={(event) => patchDraft({ stopAfterTask: event.target.checked })} />
                  任务结束默认 stop profile
                </label>
              </div>
            </Panel>
          </div>
        </div>
        </div>
      </div>

      {pendingSave ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <AlertTriangle size={16} />
                确认保存运行配置
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-600">保存后会写入 D1 app_config；敏感的 Cloudflare token 和 D1 token 仍不在控制台保存。</p>
            </div>
            <div className="space-y-3 p-4 text-xs">
              <div>
                <div className="mb-2 font-semibold text-slate-600">变更区域</div>
                <div className="flex flex-wrap gap-2">
                  {pendingSave.areas.map((area) => (
                    <Badge key={area} className="bg-amber-50 text-amber-700">
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
              <details className="rounded-md border bg-slate-50 p-2">
                <summary className="cursor-pointer font-medium text-slate-600">保存后的 app_config 预览</summary>
                <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded bg-slate-950 p-3 font-mono text-[11px] leading-5 text-slate-50 [overflow-wrap:anywhere]">
                  {JSON.stringify(pendingSave.config, null, 2)}
                </pre>
              </details>
            </div>
            <div className="flex justify-end gap-2 border-t p-3">
              <Button disabled={mutation.isPending} onClick={() => setPendingSave(null)}>
                取消
              </Button>
              <Button disabled={mutation.isPending} onClick={() => mutation.mutate(pendingSave.config)} className="bg-slate-900 text-white hover:bg-slate-800">
                <Save size={14} />
                确认保存
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
